// import axios
import axios from 'axios';
import { store } from '../store/store.js';
import { removeUser, getUser } from '../store/slices/userSlice.js';
import { openStaleBuildDialog } from '../store/slices/buildRefreshSlice.js';

const buildVersion = 'v1.2.27';

/** Response header from API; axios lowercases keys in the browser. */
const REQUIRED_FRONTEND_BUILD_HEADER = 'x-required-frontend-build';

let staleBuildDialogDispatched = false;

function notifyIfBuildMismatch(response) {
  if (staleBuildDialogDispatched) return;
  const raw = response?.headers?.[REQUIRED_FRONTEND_BUILD_HEADER];
  const server = typeof raw === 'string' ? raw.trim() : '';
  if (!server) return;
  if (server === buildVersion.trim()) return;
  staleBuildDialogDispatched = true;
  store.dispatch(openStaleBuildDialog({ serverBuildVersion: server }));
}

/**
 * Base URL for the API. Uses localhost in development, production URL otherwise.
 * @type {string}
 */
export const API_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4000/' 
  : process.env.REACT_APP_HOSTURL + '/';

// create an axios instance
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // This enables cookies to be sent with requests
});

/**
 * Gets the access token and user from the Redux store (used by the request interceptor).
 * @returns {{ accessToken?: string, email?: string, role?: string }}
 */
const getAccessTokenAndUser = () => {
  const state = store.getState();
  return getUser(state);
};

let accessToken = '';
let userRole = '';

/**
 * Clears the in-memory access token and user role. Call after logout or 401.
 * @returns {void}
 */
export const removeLocalAccessTokenAndUser = () => {
  accessToken = '';
  userRole = '';
};

// interceptor to add access token to headers
api.interceptors.request.use(
  async (config) => {
    config.headers['Build-Version'] = buildVersion;
    // Always read fresh from the store so switch-company (re-issued JWT) takes effect
    // immediately without requiring logout.
    const { accessToken: accessTokenFromStore, email, roles } = getAccessTokenAndUser();
    accessToken = accessTokenFromStore || '';
    userRole = email && Array.isArray(roles) ? `${email}_${roles.join('_')}` : '';
    if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
    if (userRole) config.headers.UserRole = userRole;
    // For a platform super-admin, tell the API which company to operate on. Read
    // fresh from the store each request so switching companies takes effect
    // immediately. Regular users must never send this — their company is JWT-only.
    const userState = store.getState().user;
    const selectedCompanyId = store.getState().tenant?.selectedCompanyId;
    const isSuperAdminUser = Array.isArray(userState?.roles) && userState.roles.includes('super_admin');
    if (isSuperAdminUser && selectedCompanyId) {
      config.headers['X-Company-Id'] = selectedCompanyId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  (response) => {
    notifyIfBuildMismatch(response);
    return response;
  },
  (error) => {
    if (error?.response) notifyIfBuildMismatch(error.response);
    return Promise.reject(error);
  },
);

/**
 * Sends a GET request. Uses shared axios instance (auth + UserRole headers).
 * @param {string} url - Path (relative to API base URL).
 * @param {Record<string, unknown>} [params={}] - Query string params.
 * @param {Record<string, unknown>} [config={}] - Extra axios config (e.g. `{ signal }` for AbortController).
 * @returns {Promise<{ data?: unknown; status?: number } | { aborted: true } | { error: true; status?: number; message: string } | { networkError: true; message: string }>}
 */
export const GET = async (url, params = {}, config = {}) => {
  try {
    // console.log('GET: ', {url}, {params});
    const res = await api.get(url, { params, ...config });
    // console.log(
    //   'RESPONSE to GET: ',
    //   res.status,
    //   JSON.stringify(res.data, null, 4),
    // );
    return {data: res.data, status: res.status};
  } catch (err) {
    // console.log('ERROR in GET: ', err);
    if (
      err?.code === 'ERR_CANCELED'
      || err?.name === 'CanceledError'
      || err?.name === 'AbortError'
    ) {
      return { aborted: true };
    }
    if (err.response) {
      if (err.response.status === 401) {
        removeLocalAccessTokenAndUser();
        store.dispatch(removeUser());
      } else if (err.response?.status === 500) {
        return {error: true, status: err.response.status, message: err.response.data?.message || 'Something went wrong, please try again later.'};
      }
      return {
        error: true,
        status: err.response.status,
        message: err.response.data?.message || 'Something went wrong, please try again later.',
      };
    } else {
      return {
        error: true, 
        message: 'Something went wrong, please try again later.'
      };
    }
  }
};

/**
 * Sends a POST request. Uses shared axios instance (auth + UserRole headers).
 * @param {string} url - Path (relative to API base URL).
 * @param {Record<string, unknown>} [params={}] - Query string params.
 * @param {Record<string, unknown>} [data={}] - Request body.
 * @returns {Promise<{ data?: unknown; status?: number } | { error: true; status?: number; message: string }>}
 */
export const POST = async (url, params = {}, data = {}) => {
  try {
    const res = await api.post(url, data, {params});
    return {data: res.data, status: res.status};
  } catch (err) {
    if (err.response) {
      if (err.response.status === 401) {
        removeLocalAccessTokenAndUser();
        store.dispatch(removeUser());
      } else if (err.response?.status === 500) {
        return {error: true, status: err.response.status, message: err.response.data?.message || 'Something went wrong, please try again later.', data: err.response.data};
      }
      return {
        error: true,
        status: err.response.status,
        message: err.response.data?.message || 'Something went wrong, please try again later.',
        data: err.response.data,
      };
    }
    return {
      error: true,
      status: err?.response?.status,
      message: err?.response?.data?.message || 'Something went wrong, please try again later.',
      data: err?.response?.data,
    };
  }
};

/**
 * Sends a PUT request. Uses shared axios instance (auth + UserRole headers).
 * @param {string} url - Path (relative to API base URL).
 * @param {Record<string, unknown>} [params={}] - Query string params.
 * @param {Record<string, unknown>} [data={}] - Request body.
 * @returns {Promise<{ data?: unknown; status?: number } | { error: true; status?: number; message: string; data?: unknown }>}
 */
export const PUT = async (url, params = {}, data = {}) => {
  try {
    const res = await api.put(url, data, {params});
    return {data: res.data, status: res.status};
  } catch (err) {
    if (err.response) {
      if (err.response.status === 401) {
        removeLocalAccessTokenAndUser();
        store.dispatch(removeUser());
      } else if (err.response?.status === 500) {
        return {error: true, status: err.response.status, message: err.response.data?.message || 'Something went wrong, please try again later.', data: err.response.data};
      }
      return {
        error: true,
        status: err.response.status,
        message: err.response.data?.message || 'Something went wrong, please try again later.',
        data: err.response.data,
      };
    }
    return {
      error: true,
      status: err?.response?.status,
      message: err?.response?.data?.message || 'Something went wrong, please try again later.',
    };
  }
};

/**
 * Sends a DELETE request (query params only). Uses shared axios instance (auth + UserRole headers).
 * @param {string} url - Path (relative to API base URL).
 * @param {Record<string, unknown>} [params={}] - Query string params.
 * @returns {Promise<{ data?: unknown; status?: number } | { error: true; status?: number; message: string }>}
 */
export const DELETE = async (url, params = {}) => {
  try {
    const res = await api.delete(url, {params});
    return {data: res.data, status: res.status};
  } catch (err) {
    if (err.response) {
      if (err.response.status === 401) {
        removeLocalAccessTokenAndUser();
        store.dispatch(removeUser());
      } else if (err.response?.status === 500) {
        return {error: true, status: err.response.status, message: err.response.data?.message || 'Something went wrong, please try again later.'};
      }
      return {
        error: true,
        status: err.response.status,
        message: err.response.data?.message || 'Something went wrong, please try again later.',
      };
    }
    return {
      error: true,
      status: err?.response?.status,
      message: err?.response?.data?.message || 'Something went wrong, please try again later.',
    };
  }
};

/**
 * Sends a PATCH request. Uses shared axios instance (auth + UserRole headers).
 * @param {string} url - Path (relative to API base URL).
 * @param {Record<string, unknown>} [params={}] - Query string params.
 * @param {Record<string, unknown>} [data={}] - Request body.
 * @returns {Promise<{ data?: unknown; status?: number } | { error: true; status?: number; message: string }>}
 */
export const PATCH = async (url, params = {}, data = {}) => {
  try {
    const res = await api.patch(url, data, {params});
    return {data: res.data, status: res.status};
  } catch (err) {
    if (err.response) {
      if (err.response.status === 401) {
        removeLocalAccessTokenAndUser();
        store.dispatch(removeUser());
      } else if (err.response?.status === 500) {
        return {error: true, status: err.response.status, message: err.response.data?.message || 'Something went wrong, please try again later.'};
      }
      return {
        error: true,
        status: err.response.status,
        message: err.response.data?.message || 'Something went wrong, please try again later.',
      };
    }
    return {
      error: true,
      status: err?.response?.status,
      message: err?.response?.data?.message || 'Something went wrong, please try again later.',
    };
  }
};

/**
 * Sends a DELETE request with optional query params and body. Uses shared axios instance (auth + UserRole headers).
 * @param {{ url: string; params?: Record<string, unknown>; data?: Record<string, unknown> }} options
 * @param {string} options.url - Path (relative to API base URL).
 * @param {Record<string, unknown>} [options.params={}] - Query string params.
 * @param {Record<string, unknown>} [options.data={}] - Request body.
 * @returns {Promise<{ data?: unknown; status?: number } | { error: true; status?: number; message: string }>}
 */
export const deleteRequest = async ({url, params = {}, data = {}}) => {
  try {
    const res = await api.delete(url, {params, data});
    return {data: res.data, status: res.status};
  } catch (err) {
    if (err.response) {
      if (err.response.status === 401) {
        removeLocalAccessTokenAndUser();
        store.dispatch(removeUser());
      } else if (err.response?.status === 500) {
        return {error: true, status: err.response.status, message: err.response.data?.message || 'Something went wrong, please try again later.'};
      }
      return {
        error: true,
        status: err.response.status,
        message: err.response.data?.message || 'Something went wrong, please try again later.',
      };
    }
    return {
      error: true,
      status: err?.response?.status,
      message: err.response.data?.message || 'Something went wrong, please try again later.',
    };
  }
};

/**
 * Sends a POST request with multipart/form-data (e.g. file upload). Uses shared axios instance (auth + UserRole headers).
 * @param {string} url - Path (relative to API base URL).
 * @param {FormData} formData - Form data to send (Content-Type: multipart/form-data).
 * @returns {Promise<{ data?: unknown; status?: number } | { error: true; status?: number; message: string }>}
 */
export const UPLOAD = async (url, formData) => {
  try {
    const res = await api.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return {data: res.data, status: res.status};
  } catch (err) {
    if (err.response) {
      if (err.response.status === 401) {
        removeLocalAccessTokenAndUser();
        store.dispatch(removeUser());
      } else if (err.response?.status === 500) {
        return {error: true, status: err.response.status, message: err.response.data?.message || 'Something went wrong, please try again later.'};
      }
      return {
        error: true,
        status: err.response.status,
        message: err.response.data?.message || 'Something went wrong, please try again later.',
      };
    }
    return {
      error: true,
      status: err?.response?.status,
      message: err?.response?.data?.message || 'Something went wrong, please try again later.',
    };
  }
};
